import { INLINE_LINK } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { LegalPage } from '@/features/legal/LegalPage';
import { useLocalizedSeoMeta } from '@/lib/seo/use-localized-seo-meta';

import { ComplianceCard } from '../../features/trust/components/ComplianceCard';
import {
  complianceFrameworks,
  complianceStatusDocument,
} from '../../features/trust/content/compliance-status';

import type { ReactElement } from 'react';

/**
 * Trust Center — compliance page (`/trust/compliance`). Phase 6
 * Pass 3 iter 245.
 *
 * Per `pass-3-trust-center.md` § Pages → `/trust/compliance`:
 * "GDPR, EU AI Act, MENA, CCPA / state laws, certifications
 * status" + § "Compliance status driven by JSON config": "The
 * Trust Center compliance page renders cards from this JSON.
 * Status strings drive badge colors. Editor commits a PR to
 * update; site rebuilds on merge."
 *
 * **Marketing voice (Marketer hat, locked)**: leads with the
 * last-updated marker so a procurement reader can confirm
 * freshness. Each card has a concrete target date (where one
 * exists) rather than aspirational adjectives. Out-of-scope
 * frameworks (HIPAA / FedRAMP) are explicit, not hidden — saves
 * an email exchange.
 *
 * **Layout (Designer hat, locked)**: single-column on mobile;
 * two-column grid on `lg:` to fit 8 cards in ~4 rows on desktop.
 * Cards are uniform; no special treatment for any framework
 * because procurement readers expect uniform information per row.
 *
 * **i18n**: page chrome (heading, intro, last-updated, contact)
 * resolves through `useT()` under the `compliance.*` namespace.
 * This is a short hub/links page, not a binding legal document, so
 * it carries no `LegalTranslationNotice`. Per-framework card copy
 * is JSON-driven content rendered by `ComplianceCard`.
 *
 * **Three-layer contract preserved**: pure composition. Data from
 * the JSON loader. No state.
 */
export function Compliance(): ReactElement {
  const { t } = useT();
  useLocalizedSeoMeta({
    title: t('compliance.meta.title'),
    description: t('compliance.meta.description'),
    path: '/trust/compliance',
  });
  return (
    <LegalPage
      breadcrumb={t('compliance.breadcrumb')}
      title={t('compliance.title')}
      intro={t('compliance.intro')}
      width="default"
    >
      {/* Its own revision stamp rather than the shared `LegalLastUpdated`, for
          the same reason `/trust/subprocessors` keeps one: the date comes from
          `complianceStatusDocument` and the sentence continues into the
          escalation address. */}
      <p className="mb-8 text-sm text-[color:var(--bd-muted)]">
        {t('compliance.lastUpdated')}{' '}
        <time dateTime={complianceStatusDocument.lastUpdated}>
          {complianceStatusDocument.lastUpdated}
        </time>
        . {t('compliance.questions')}{' '}
        <a className={INLINE_LINK} href="mailto:admin@syncanix.com">
          admin@syncanix.com
        </a>
        .
      </p>

      <section
        aria-labelledby="compliance-frameworks"
        className="grid grid-cols-1 gap-4 lg:grid-cols-2"
      >
        {/* sr-only h2 so the outline is h1 → h2 → card h3 (no skipped level, I148) */}
        <h2 id="compliance-frameworks" className="sr-only">
          {t('compliance.frameworksLabel')}
        </h2>
        {complianceFrameworks.map(([key, framework]) => (
          <ComplianceCard key={key} framework={framework} />
        ))}
      </section>
    </LegalPage>
  );
}
